import React, { useContext, useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Image, ImageStyle, Linking, Platform, View } from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'
import { InAppBrowser } from 'react-native-inappbrowser-reborn'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Config from 'react-native-config'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Text, Toggle } from '@components/base'
import { Feature, InlineAlert, ScrollViewWithFade, Section, useFeatureFlag } from '@components'
import { NavigationContainer } from '@src/screens/Common/containers'
import { AppRouteProp, AppStackParamList } from '@src/navigation/types'
import { NavigatorContext, useSnack } from '@src/utils/navigatorContext'
import { terraProviderSelector } from '@src/selectors/integrations'
import { ErrorMessages, Logger, LoggerScreens, Device } from '@src/config'
import { ListProviders, TerraProviderKind, UiInlineAlert, UiStateNames } from '@src/types'
import { useShouldShowByosOnboarding } from '@src/screens/OwnSensorOnboarding/hooks'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import { SectionItem } from '@src/screens/Profile'
import { useGoBack } from '@src/utils'
import { IconSources } from '@src/assets/icons'
import { useTerraHealthConnect } from '../../utils/hooks'
import { GenerateTerraAuthUrlQuery } from '../../graphql/generateTerraAuthUrl.generated'
import { hasProviderBrand, providerBrandIcon, providerBrandName } from './providerBranding'
import {
  getTerraAuthFailureMessage,
  getTerraAuthRedirectUrl,
  handleTerraAuthResult,
} from './terraAuth'
import { getAvailableTerraProviderTypes } from './terraProviderUtils'

const scheme = Config.APP_SCHEME

enum TERRA_DATA_TYPES {
  ExerciseActivity = 'exercise_activity',
  StepsCountMeasurement = 'steps_count_measurement',
  MeditationActivity = 'meditation_activity',
  SleepActivity = 'sleep_activity',
  RunningDistanceMeasurement = 'running_distance_measurement',
  BodyFatPercentageMeasurement = 'body_fat_percentage_measurement',
  FlightsClimbedMeasurement = 'flights_climbed_measurement',
  ExerciseTimeMeasurement = 'exercise_time_measurement',
  WeightMeasurement = 'weight_measurement',
  CyclingDistanceMeasurement = 'cycling_distance_measurement',
  BloodGlucoseMeasurement = 'glucose_measurement',
  Meals = 'meals',
  KetoneMeasurement = 'ketone_measurement',
}

const isAndroid13OrLower = Device.android && Number(Platform.Version) <= 33

const TERRA_DATA_TYPES_TITLES: { [key in TERRA_DATA_TYPES]: string } = {
  [TERRA_DATA_TYPES.ExerciseActivity]: 'Exercises',
  [TERRA_DATA_TYPES.StepsCountMeasurement]: 'Steps',
  [TERRA_DATA_TYPES.MeditationActivity]: 'Meditation',
  [TERRA_DATA_TYPES.SleepActivity]: 'Sleep',
  [TERRA_DATA_TYPES.RunningDistanceMeasurement]: 'Running Distance',
  [TERRA_DATA_TYPES.BodyFatPercentageMeasurement]: 'Body fat percentage',
  [TERRA_DATA_TYPES.FlightsClimbedMeasurement]: 'Flights Climbed',
  [TERRA_DATA_TYPES.ExerciseTimeMeasurement]: 'Exercise Time',
  [TERRA_DATA_TYPES.WeightMeasurement]: 'Weight',
  [TERRA_DATA_TYPES.CyclingDistanceMeasurement]: 'Cycling Distance',
  [TERRA_DATA_TYPES.BloodGlucoseMeasurement]: 'Blood Glucose',
  [TERRA_DATA_TYPES.Meals]: 'Meals',
  [TERRA_DATA_TYPES.KetoneMeasurement]: 'Blood Ketones',
}

export const TerraProvider = () => {
  const route = useRoute<AppRouteProp<'TerraProvider'>>()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const findProvider = useSelector(terraProviderSelector)
  const providerId = route.params.provider
  const provider = findProvider(providerId)
  const isAppleHealth =
    provider.providerKind === TerraProviderKind.AppleHealth || providerId === 'apple_health'
  const dispatch = useDispatch()
  const hasProvider = !!provider.provider
  const [failedProviderId, setFailedProviderId] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    if (isAppleHealth) {
      navigation.replace(Device.hasHealthKit ? 'HealthKitIntegration' : 'Integrations')
      return
    }
    if (hasProvider) {
      return
    }

    let cancelled = false
    dispatch({
      type: 'settings/fetchTerraProviders',
      success: (response: ListProviders) => {
        if (!cancelled && !response.providers.some((item) => item.provider === providerId)) {
          navigation.replace('Integrations')
        }
      },
      failure: () => {
        if (!cancelled) {
          setFailedProviderId(providerId)
        }
      },
    })
    return () => {
      cancelled = true
    }
  }, [dispatch, hasProvider, isAppleHealth, navigation, providerId, retryCount])

  if (isAppleHealth) {
    return null
  }
  if (!hasProvider) {
    return (
      <NavigationContainer title="Integration">
        <LoadingContainer
          loadingMessage="Loading integration"
          error={failedProviderId === providerId}
          errorMessage="Unable to load integration. Please try again."
        >
          <Button
            type="primary"
            size="block"
            accessibilityLabel="Retry"
            onPress={() => {
              setFailedProviderId(null)
              setRetryCount((count) => count + 1)
            }}
          >
            Retry
          </Button>
        </LoadingContainer>
      </NavigationContainer>
    )
  }
  return <TerraProviderContent key={providerId} />
}

const TerraProviderContent = () => {
  const route = useRoute<AppRouteProp<'TerraProvider'>>()

  // TODO: change provider to be a TerraProvider type
  const { provider: providerId, cancel: isDeleteConfirmed } = route.params

  const dispatch = useDispatch()
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const findTerraProvider = useSelector(terraProviderSelector)
  const provider = findTerraProvider(providerId)

  // User-facing brand name (e.g. "Garmin Connect™"); falls back to provider.name.
  const displayName = providerBrandName(provider.providerKind, provider.name)

  const goBack = useGoBack()

  const showSnack = useSnack()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const insets = useSafeAreaInsets()

  const styles = useStyleSheet(themedStyles)
  const [disabledTypes, setDisabledTypes] = useState(provider.disabledTypes || [])
  const [providerConnected, setProviderConnected] = useState(provider.active)
  const [healthConnectAuthInProgress, setHealthConnectAuthInProgress] = useState(false)
  const authInProgress = useRef<boolean>(false)
  const deauthLoadingRef = useRef(false)
  const isOnboarding = useShouldShowByosOnboarding()

  const initHealthConnectConnection = useTerraHealthConnect()

  const authRedirectUrl = getTerraAuthRedirectUrl(scheme)

  const setAuthInProgress = (authState: boolean) => {
    authInProgress.current = authState
  }

  const mfpPromotionEnabled = useFeatureFlag(Feature.MfpPromotion)

  useEffect(
    () => () => {
      if (deauthLoadingRef.current) {
        disableLoader()
        deauthLoadingRef.current = false
      }
    },
    [disableLoader],
  )

  const onSkipPress = () => {
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.OwnSensorOnboardingCompleted,
        value: true,
      },
    })

    navigation.reset({
      index: 0,
      routes: [{ name: 'Drawer' }],
    })
  }

  // Auth flow for Terra providers consists on three steps
  // 0. user press toggle which trigger initRegularAuthFlow
  // 1. initRegularAuthFlow receive auth url from backend via settings/generateTerraAuthUrl
  // 2. user need to go through provider auth process in InAppBrowser which is handled by this method
  // 3. after user completed auth in browser backend receives webhook with user info and
  //    we refetch providers list from backend
  const initRegularAuthFlow = (providerId: string) => {
    setAuthInProgress(true)
    setProviderConnected(true)
    dispatch({
      type: 'settings/generateTerraAuthUrl',
      payload: { provider: providerId, authRedirectUrl },
      success: (response: GenerateTerraAuthUrlQuery['generateTerraAuthUrl']) => {
        openProviderAuth(response.url)
      },
      failure: () => {
        setAuthInProgress(false)
        setProviderConnected(false)
        showSnack(ErrorMessages.ServerError, null, 'error')
      },
    })
  }

  const openProviderAuth = async (url: string) => {
    try {
      if (await InAppBrowser.isAvailable()) {
        const result = await InAppBrowser.openAuth(url, authRedirectUrl, {
          // do not close browser on Android when app goes to background
          // https://github.com/proyecto26/react-native-inappbrowser/issues/213
          showInRecents: true,
        })
        handleTerraAuthResult(result, {
          onSuccess: () => {
            setAuthInProgress(false)
            showSnack(`${displayName} sync turned on`, null, 'success')
            dispatch({ type: 'settings/fetchTerraProviders' })
            goBack()
          },
          onFailure: (reason) => {
            setAuthInProgress(false)
            setProviderConnected(false)
            Logger.sendError(LoggerScreens.TerraProvider, ErrorMessages.TerraAuthorizationFailed, {
              reason,
              providerName: provider.name,
            })
            showSnack(getTerraAuthFailureMessage(displayName, reason), null, 'error')
          },
          onCancel: () => {
            if (authInProgress.current) {
              setAuthInProgress(false)
              setProviderConnected(false)
            }
          },
        })
      }
    } catch (error: any) {
      setAuthInProgress(false)
      setProviderConnected(false)
      const errorMessage = error.message ? error.message : ErrorMessages.TerraAuthorizationFailed
      Logger.sendError(LoggerScreens.TerraProvider, errorMessage, {
        error,
        providerName: provider.name,
      })
      showSnack(errorMessage, null, 'error')
    }
  }

  const toggleDataType = (dataType: TERRA_DATA_TYPES) => {
    if (!disabledTypes.includes(dataType)) {
      setDisabledTypes([...disabledTypes, dataType])
    } else {
      setDisabledTypes(disabledTypes.filter((type) => type !== dataType))
    }
  }

  const onDataTypeToggle = (dataType: TERRA_DATA_TYPES) => {
    toggleDataType(dataType)
    dispatch({
      type: 'settings/toggleProviderDataType',
      payload: { provider: providerId, dataType },
      failure: () => {
        toggleDataType(dataType)
        showSnack(ErrorMessages.ServerError, null, 'error')
      },
    })
  }

  const isDataTypeEnabled = (dataType: TERRA_DATA_TYPES) =>
    provider.active && !disabledTypes.includes(dataType)

  const renderDataTypes = () => {
    const availableTypes = getAvailableTerraProviderTypes(
      provider.availableTypes,
    ) as TERRA_DATA_TYPES[]

    return (
      <View style={Device.ios && styles.dataTypes}>
        {availableTypes.map((dataType) => (
          <Section title={TERRA_DATA_TYPES_TITLES[dataType]} key={dataType}>
            <Toggle
              disabled={!provider.active}
              checked={isDataTypeEnabled(dataType)}
              onChange={() => onDataTypeToggle(dataType)}
            />
          </Section>
        ))}
      </View>
    )
  }

  const confirmSyncDelete = () => {
    navigation.navigate('CancelModal', {
      title: `Are you sure you want to delete synchronization with ${displayName}?`,
      cancelText: 'No, Keep it synced',
      confirmText: 'Yes, delete synchronization',
      parentScreen: 'TerraProvider',
    })
  }

  useEffect(() => {
    if (!isDeleteConfirmed || deauthLoadingRef.current) {
      return
    }

    deauthLoadingRef.current = true
    // The confirmed route parameter intentionally drives the local toggle state immediately.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setProviderConnected(false)
    enableLoader()

    dispatch({
      type: 'settings/deauthTerraProvider',
      payload: { provider: providerId },
      success: () => {
        showSnack(`${displayName} sync turned off`)
      },
      complete: () => {
        disableLoader()
        deauthLoadingRef.current = false
      },
      failure: () => {
        showSnack(ErrorMessages.ServerError, null, 'error')
      },
    })
  }, [providerId, isDeleteConfirmed, dispatch, showSnack, displayName, enableLoader, disableLoader])

  const onEnableHealthConnectPress = async () => {
    setAuthInProgress(true)
    setProviderConnected(true)
    setHealthConnectAuthInProgress(true)

    const result = await initHealthConnectConnection()

    if (!result) {
      setAuthInProgress(false)
      setProviderConnected(false)
      setHealthConnectAuthInProgress(false)
      showSnack(ErrorMessages.ServerError, null, 'error')
      return
    }

    setHealthConnectAuthInProgress(false)

    goBack()
  }

  const displayHealthConnectInstallBanner =
    isAndroid13OrLower &&
    provider.providerKind === TerraProviderKind.HealthConnect &&
    !provider.active

  const displayHealthConnectInfo =
    Device.android && provider.providerKind === TerraProviderKind.HealthConnect && provider.active

  if (healthConnectAuthInProgress) {
    return <LoadingContainer />
  }

  // TODO: Extract SectionItem (used for MFP) to a generic component
  return (
    <NavigationContainer title={displayName} showLoadingIndicator>
      <View style={[styles.container, { paddingBottom: insets.bottom }]}>
        {hasProviderBrand(provider.providerKind) && (
          <View style={styles.brandHeader}>
            <Image
              source={providerBrandIcon(provider.providerKind, IconSources.garminIcon)}
              style={styles.brandTile as ImageStyle}
            />
            <Text type="regular" bold style={styles.brandName}>
              {displayName}
            </Text>
          </View>
        )}
        {displayHealthConnectInstallBanner && (
          <InlineAlert
            category={UiInlineAlert.Info}
            message={
              <View>
                <Text type="regular">
                  Before enabling the Health Connect integration, install the Health Connect app
                  from{' '}
                  <Text
                    type="regular"
                    style={styles.enableHealthConnectLink}
                    onPress={() => {
                      Linking.openURL('market://details?id=com.google.android.apps.healthdata')
                    }}
                  >
                    Play Store
                  </Text>
                  .
                </Text>
              </View>
            }
            style={styles.alert}
          />
        )}
        {displayHealthConnectInfo && (
          <InlineAlert
            category={UiInlineAlert.Info}
            message={
              <View>
                <Text type="regular">
                  Note you must give your health tracking apps permission to sync data within the
                  Health Connect app.{' '}
                  <Text
                    type="regular"
                    style={styles.enableHealthConnectLink}
                    onPress={() => {
                      Linking.openURL(
                        'https://support.google.com/android/answer/13770320?sjid=13615958825016007214-NA',
                      )
                    }}
                  >
                    Learn more
                  </Text>
                </Text>
              </View>
            }
            style={styles.alert}
          />
        )}
        {mfpPromotionEnabled &&
          provider.providerKind === TerraProviderKind.MyFitnessPal &&
          !provider.active && (
            <>
              <Section title="Instructions" style={styles.section} />
              <SectionItem
                hasBottomBorder
                icon="key"
                onPress={() => {
                  navigation.navigate('MfpInstructions')
                }}
              >
                <Text type="regular">Set your MyFitnessPal Diary Key</Text>
              </SectionItem>
            </>
          )}
        <Section title="Integration" style={styles.section} />
        <View>
          <Section title={`${displayName} Sync`}>
            <Toggle
              checked={providerConnected || provider.active}
              onChange={() => {
                if (providerConnected || provider.active) {
                  confirmSyncDelete()
                } else {
                  if (provider.providerKind === TerraProviderKind.HealthConnect) {
                    onEnableHealthConnectPress()
                  } else {
                    initRegularAuthFlow(provider.provider)
                  }
                }
              }}
            />
          </Section>
        </View>

        <Section title="Data" style={styles.section} />
        <ScrollViewWithFade>{renderDataTypes()}</ScrollViewWithFade>
        {Device.ios && (
          <InlineAlert
            message={
              'If Apple Health is enabled, there may be conflicting measurements with this data source. ' +
              'We suggest disabling whatever data source you believe will be inaccurate.'
            }
            style={styles.alert}
          />
        )}
        {isOnboarding && !provider.active && (
          <View style={styles.buttonContainer}>
            <Button
              type="outline"
              size="block"
              onPress={onSkipPress}
              style={styles.skipButton}
              accessibilityLabel="Skip"
            >
              Skip for Now
            </Button>
          </View>
        )}
      </View>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  brandHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    gap: 8,
  },
  brandTile: {
    width: 28,
    height: 28,
    borderRadius: 6,
  },
  brandName: {
    color: 'theme.text.secondary',
  },
  section: {
    height: 40,
    backgroundColor: 'theme.surface.base2',
  },
  alert: {
    margin: 16,
  },
  dataTypes: {
    paddingBottom: 16,
  },
  enableHealthConnectLink: {
    color: 'theme.text.link',
  },
  buttonContainer: {
    padding: 16,
  },
  skipButton: {
    marginBottom: 8,
  },
})
